// Lightweight analytics helper — logs to the YuBlink backend + optional Plausible.
import api from './api';

const DEBOUNCE_MS = 300;
const lastSent = new Map();

// Session ID — stable per tab/visit, resets when the browser tab closes (sessionStorage).
// This is the key for computing session duration server-side.
const SESSION_KEY = 'yublink_session_id';
function getSessionId() {
  if (typeof window === 'undefined') return null;
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = (crypto?.randomUUID?.() || `s_${Date.now()}_${Math.random().toString(36).slice(2)}`);
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return null;
  }
}

// Time-on-page tracking — timestamp of last trackPageView to compute duration on next route change / tab hide.
let currentPagePath = null;
let currentPageEnteredAt = 0;

function flushPageExit(nextPath = null) {
  if (!currentPagePath || !currentPageEnteredAt) return;
  const duration_ms = Math.max(0, Date.now() - currentPageEnteredAt);
  // Ignore sub-1s blips — usually double-render artifacts
  if (duration_ms < 1000) { currentPagePath = null; currentPageEnteredAt = 0; return; }
  const payload = {
    name: 'page_exit',
    props: { duration_ms, next_path: nextPath || null },
    path: currentPagePath,
    session_id: getSessionId(),
  };
  // Use sendBeacon when available so we don't lose the event on unload
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
      navigator.sendBeacon(`${base}/api/events/track`, blob);
    } else {
      api.post('/events/track', payload).catch(() => {});
    }
  } catch { /* noop */ }
  currentPagePath = null;
  currentPageEnteredAt = 0;
}

// Register global listeners once (hide/unload)
if (typeof window !== 'undefined' && !window.__yb_analytics_bound) {
  window.__yb_analytics_bound = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushPageExit(null);
  });
  window.addEventListener('pagehide', () => flushPageExit(null));
}

export function track(name, props = {}, path = null) {
  if (!name) return;

  // Debounce identical fire-storms (e.g. React double-effect in dev)
  const key = `${name}|${path || ''}`;
  const now = Date.now();
  const prev = lastSent.get(key) || 0;
  if (now - prev < DEBOUNCE_MS) return;
  lastSent.set(key, now);

  // Backend event log (owned-by-you analytics)
  const payload = {
    name,
    props,
    path: path || (typeof window !== 'undefined' ? window.location.pathname : ''),
    session_id: getSessionId(),
  };
  api.post('/events/track', payload).catch(() => { /* fire-and-forget */ });

  // Optional Plausible custom event (if the script has loaded)
  if (typeof window !== 'undefined' && typeof window.plausible === 'function' && name !== 'page_view') {
    try { window.plausible(name, { props }); } catch { /* noop */ }
  }
}

export function trackPageView(path) {
  // Emit page_exit for the previous page first
  if (currentPagePath && currentPagePath !== path) flushPageExit(path);
  currentPagePath = path;
  currentPageEnteredAt = Date.now();
  track('page_view', {}, path);
}
